import { useQueries, UseQueryResult } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useIsCOMSWriteEnabled } from ':cloud/hooks/featureFlags/useIsCOMSWriteEnabled';
import { useGetAccount } from ':cloud/queries/BillingQueries/useGetAccount';
import { generateAccountItemsQuery } from ':cloud/queries/BillingQueries/useGetAccountItems';
import { generateOrganizationQuery } from ':cloud/queries/OrganizationQueries/useGetAllOrganizations';
import {
  FormattedOrganizationResponse,
  generateOrganizationResponse,
} from ':cloud/queries/OrganizationQueries/useGetOrganization';
import { generateProjectsQuery } from ':cloud/queries/Projects/useGetProjects';
import {
  generateResourcesQuery,
  generateResourcesResponse,
} from ':cloud/queries/ResourceQueries/useGetResources';
import {
  generatePermissionsResponse,
  generateUserPermissionsQueries,
  generateUserPermissionsQueriesLEGACY,
  Permissions,
} from ':cloud/queries/UserQueries/useGetUserPermissions';
import {
  GetAccountItemsResponse,
  GetOrganization,
  GetOrganizationsResponse,
  GetProjectsResponse,
} from ':cloud/types/service_proto';
import { AccountItem, ICluster } from ':cloud/types/ts_types';
import { CustomNetworkError } from ':cloud/utils/errors';

type QueriesType = [
  UseQueryResult<boolean>,
  UseQueryResult<boolean>,
  UseQueryResult<boolean>,
  UseQueryResult<boolean>,
  UseQueryResult<GetOrganizationsResponse>,
  UseQueryResult<GetProjectsResponse>,
  UseQueryResult<{ resources: ICluster[] }>,
  UseQueryResult<GetAccountItemsResponse>,
];

type UseGetAppStartDataProps = {
  userId?: string;
  orgId?: string;
};

/*
 * combines all queries that need to run at initial app load
 * these are run in parralel and will block the app from loading until complete
 */
export function useGetAppStartData({ userId = '', orgId = '' }: UseGetAppStartDataProps): {
  isLoading: boolean;
  permissions: Permissions;
  organization: FormattedOrganizationResponse;
  projects: GetProjectsResponse;
  resources: ICluster[];
  accountItems: AccountItem[];
} {
  // needs to happen before any other billing calls, b/c this does a find-or-create and will create an account if once doesn't exist
  const { isLoading: isAccountLoading } = useGetAccount(orgId);
  const shouldUseCOMSWriteEndpoint = useIsCOMSWriteEnabled();

  // @ts-expect-error issue w/ RQ useQueries typing
  const queries: QueriesType = useQueries({
    queries: [
      ...(shouldUseCOMSWriteEndpoint
        ? generateUserPermissionsQueries(orgId)
        : generateUserPermissionsQueriesLEGACY(orgId)),
      generateOrganizationQuery({
        userId,
      }),
      generateProjectsQuery({
        organizationId: orgId,
      }),
      generateResourcesQuery(orgId),
      generateAccountItemsQuery({
        orgId,
        enabled: !!orgId && !isAccountLoading,
      }),
    ],
  });

  const currentOrgResponse = queries[4]?.data?.find(
    (item) => item.organizationId === orgId,
  ) as GetOrganization;

  const error = queries.find((query) => query.isError);
  if (error) {
    const status = (error?.error as AxiosError).response?.status;
    // 401s indicate expired session and don't need to throw an error
    // user will be shown a modal to re-authenticate
    if (status !== 401) {
      throw new CustomNetworkError('Error from useGetAppStartData', status as number);
    }
  }

  return {
    isLoading: queries.some((query) => query.isLoading),
    permissions: generatePermissionsResponse(queries.slice(0, 4) as { data?: boolean }[]),
    organization: generateOrganizationResponse(currentOrgResponse?.organizationDetails) || [],
    projects: queries[5]?.data || [],
    resources: generateResourcesResponse(
      queries[6]?.data?.resources,
      (r: ICluster) => r.clusterType === 'qt',
    ),
    accountItems: queries[7]?.data?.accountItems || [],
  };
}
